import { Card, Form, Input, Button, message, Typography } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authApi } from '@tradermind/shared/api';
import { changePasswordSchema, type ChangePasswordFormData } from '@tradermind/shared/validators';

const { Title, Text } = Typography;

export default function ChangePasswordPage() {
  const { t } = useTranslation('profile');
  const navigate = useNavigate();
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ChangePasswordFormData>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: {
      old_password: '',
      new_password: '',
      confirm_password: '',
    },
  });

  const changePasswordMutation = useMutation({
    mutationFn: authApi.changePassword,
    onSuccess: () => {
      message.success(t('change_password_page.success'));
      reset();
      navigate('/profile');
    },
    onError: (error: any) => {
      message.error(error?.error?.message || t('change_password_page.failed'));
    },
  });

  const onSubmit = (data: ChangePasswordFormData) => {
    changePasswordMutation.mutate({
      old_password: data.old_password,
      new_password: data.new_password,
    });
  };

  return (
    <div style={{ maxWidth: 600, margin: '0 auto' }}>
      <Title level={4}>{t('change_password_page.title')}</Title>

      <Card>
        <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
          <Form.Item
            label={t('change_password_page.current_password')}
            validateStatus={errors.old_password ? 'error' : ''}
            help={errors.old_password?.message}
          >
            <Controller
              name="old_password"
              control={control}
              render={({ field }) => (
                <Input.Password
                  {...field}
                  prefix={<LockOutlined />}
                  placeholder={t('change_password_page.current_password_placeholder')}
                  size="large"
                />
              )}
            />
          </Form.Item>

          <Form.Item
            label={t('change_password_page.new_password')}
            validateStatus={errors.new_password ? 'error' : ''}
            help={errors.new_password?.message}
          >
            <Controller
              name="new_password"
              control={control}
              render={({ field }) => (
                <Input.Password
                  {...field}
                  prefix={<LockOutlined />}
                  placeholder={t('change_password_page.new_password_placeholder')}
                  size="large"
                />
              )}
            />
          </Form.Item>

          <Form.Item
            label={t('change_password_page.confirm_password')}
            validateStatus={errors.confirm_password ? 'error' : ''}
            help={errors.confirm_password?.message}
          >
            <Controller
              name="confirm_password"
              control={control}
              render={({ field }) => (
                <Input.Password
                  {...field}
                  prefix={<LockOutlined />}
                  placeholder={t('change_password_page.confirm_password_placeholder')}
                  size="large"
                />
              )}
            />
          </Form.Item>

          <div style={{ marginTop: 24 }}>
            <Text type="secondary">{t('change_password_page.requirements')}</Text>
            <ul style={{ marginTop: 8, color: '#8c8c8c' }}>
              <li>{t('change_password_page.req_min_length')}</li>
              <li>{t('change_password_page.req_complexity')}</li>
            </ul>
          </div>

          <Form.Item style={{ marginTop: 24 }}>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              block
              loading={changePasswordMutation.isPending}
            >
              {t('change_password_page.submit')}
            </Button>
            <Button
              size="large"
              block
              style={{ marginTop: 8 }}
              onClick={() => navigate('/profile')}
            >
              {t('common:cancel')}
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
}
